import React from "react";
import ValidateInput from "../forms/components/ValidateInput";
import { injectIntl, intlShape } from "react-intl";

const numeral = require("numeral");
const { Field } = require("redux-form");

const stripNonDigits = input => input.replace(/\D/g, "");
const style = require("./style.module.scss");

interface MoneyInputProps {
  className?: string;
  clearable?: boolean;
  id?: string;
  min?: string;
  max?: string;
  name: string;
  onChange: Function;
  onFocus?: React.FocusEventHandler<{}>;
  onBlur?: React.FocusEventHandler<{}>;
  onKeyDown?: React.KeyboardEventHandler<{}>;
  value: string;
  placeholder?: string;
  type?: string;
  decimals?: boolean;
  intl: intlShape;
  allowNegatives?: boolean;
  required?: boolean;

  _onFocus?: Function;
  withRef?: boolean;
  getRef?: Function;
  autoComplete?: string;
}

class MoneyInput extends React.Component<MoneyInputProps, any> {
  public static defaultProps = {
    value: "",
    placeholder: "",
    decimals: true,
    autoComplete: "off",
  };

  constructor(props) {
    super(props);
    this.state = {
      value: this.toCurrency(props.value) || "",
      isNegative: MoneyInput.isNegative(props.value),
    };

    this.toCurrency = this.toCurrency.bind(this);
    this.handleOnChange = this.handleOnChange.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.handleChangePosNeg = this.handleChangePosNeg.bind(this);
  }

  static isNegative(value) {
    if (!value) {
      return false;
    }
    return (value.length > 0 && value[0] === "-") || value < 0;
  }

  componentWillReceiveProps(nextProps) {
    let isNegative = this.state.isNegative;
    if (nextProps.value !== "" || nextProps.value !== 0) {
      // isNegative is sticky until we get an actual value that is not empty
      isNegative = MoneyInput.isNegative(nextProps.value);
    } else if (this.state.isNegative && this.props.value !== nextProps.value) {
      // only reset on zero if the value changed to zero/empty
      isNegative = false;
    }
    this.setState({
      ...this.state,
      isNegative,
      value: this.toCurrency(nextProps.value) || "",
    });
  }

  formatNumber(num) {
    const { decimals } = this.props;
    let value = stripNonDigits(num);

    if (decimals) {
      value /= 100;
    }

    if (isNaN(value) || this.valueBelowMin(value) || this.valueAboveMax(value)) {
      return;
    }

    return value;
  }

  toCurrency(value) {
    if (value === null || value === undefined || value === "") {
      return "";
    }
    const { decimals, intl } = this.props;
    const options = {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    };
    if (!decimals) {
      options.maximumFractionDigits = 0;
      options.minimumFractionDigits = 0;
    }
    let formattedNumber = intl.formatNumber(value, options);
    formattedNumber = formattedNumber.replace("$", "");
    formattedNumber = formattedNumber.replace("-", "");
    return formattedNumber;
  }

  handleOnChange(ev) {
    const incomingValue = ev.target.value;

    if (this.props.clearable && incomingValue === "") {
      this.props.onChange(ev);
      return;
    }

    if (this.props.allowNegatives && (incomingValue === "-" || incomingValue === "0.00-")) {
      // special case: first character was minus, make number negative
      this.setState({
        ...this.state,
        isNegative: true,
      });
    } else {
      const prefix = this.state.isNegative ? "-" : "";
      const value = `${prefix}${this.formatNumber(incomingValue)}`;
      ev.target.value = value;
      this.props.onChange(ev);
    }
  }

  onKeyDown(ev) {
    if (this.props.onKeyDown) {
      this.props.onKeyDown(ev);
    }
  }

  valueBelowMin(value) {
    let val = value;
    if (this.state.isNegative) {
      val *= -1;
    }
    return this.props.min && parseFloat(val) < parseFloat(this.props.min);
  }

  valueAboveMax(value) {
    let val = value;
    if (this.state.isNegative) {
      val *= -1;
    }
    return this.props.max && parseFloat(val) > parseFloat(this.props.max);
  }

  handleChangePosNeg(e: React.ChangeEvent<HTMLInputElement>) {
    // only two possibilities
    const { value, name, onChange } = this.props;
    if (Number.isNaN(value)) throw Error(`${value} is not a number`);

    const finalVal = Number(value);
    const target = { name, value: 0 };
    switch (e.target.value) {
      case "$":
        target.value = Math.abs(finalVal);
        finalVal < 0 && onChange({ target, stopPropagation: () => {} });
        break;
      case "-$":
        target.value = -Math.abs(finalVal);
        finalVal > 0 && onChange({ target, stopPropagation: () => {} });
        break;
      default:
      // no op we don't want to do anything
    }
  }

  renderInput() {
    const propsClone: any = { ...this.props };
    // decimals, clearable, intl, and allowNegatives should not be part of inputProps,
    // all others are passed through
    delete propsClone.decimals;
    delete propsClone.clearable;
    delete propsClone.intl;
    delete propsClone.allowNegatives;

    const placeholder = this.props.placeholder && numeral(this.props.placeholder).format("0,0");

    const fieldProps = {
      ...propsClone,
      placeholder,
      type: "text",
      _onFocus: e => {
        if (this.props.onFocus) {
          this.props.onFocus(e);
        }
      },
      onChange: this.handleOnChange,
      onKeyDown: this.onKeyDown,
      autoComplete: this.props.autoComplete,
    };

    if (this.props.type === "validateMoney") {
      let validate = [];
      const inputRequired = value => (value ? undefined : "This field is required");
      if (propsClone.required) {
        validate = [inputRequired];
        delete propsClone.required;
      }
      return (
        <Field
          {...fieldProps}
          inputValue={this.state.value}
          component={ValidateInput}
          validate={validate}
        />
      );
    }
    delete fieldProps.withRef;
    delete fieldProps.getRef;
    delete fieldProps._onFocus;
    return <input {...fieldProps} value={this.state.value} />;
  }

  render() {
    const addonText = this.state.isNegative ? "-$" : "$";
    return (
      <div className="input-group">
        <div className={style.moneyDollarContainer}>
          <input
            className={`input-group-addon ${style.moneyDollar}`}
            onChange={this.handleChangePosNeg}
            name="dollar"
            type="text"
            value={addonText}
          />
          {this.renderInput()}
        </div>
      </div>
    );
  }
}

export default injectIntl(MoneyInput);
